import React, { useState } from 'react';
import { Button, Menu, MenuItem, ListItemIcon, ListItemText, CircularProgress, Divider, Tooltip } from '@mui/material';
import { Share, ContentCopy, Facebook, LinkedIn, Instagram, Groups } from '@mui/icons-material';
import XIcon from '@mui/icons-material/X';
import { useEventShare } from '../../../hooks/useEventShare';
import { EventStatus } from '../../../enums/EventStatus';
import { useDialogs, useNotifications } from '@toolpad/core';
import { SxProps, Theme } from '@mui/material/styles';
import { isEventShareable } from '../../../utils/eventShareability';
import ShareToCommunitiesDialog from '../../Dialogs/ShareToCommunitiesDialog';
import type { CommunityShareMode } from '../../../services/inviteService';

interface EventShareButtonProps {
  eventId: string;
  eventTitle?: string;
  eventStatus?: EventStatus;
  isInviteOnly?: boolean;
  isEventPublic?: boolean;
  communityShareMode?: CommunityShareMode;
  showCommunityShare?: boolean;
  variant?: 'text' | 'outlined' | 'contained';
  size?: 'small' | 'medium' | 'large';
  disabled?: boolean;
  sx?: SxProps<Theme>;
}

const EventShareButton: React.FC<EventShareButtonProps> = ({
  eventId,
  eventTitle,
  eventStatus,
  isInviteOnly = false,
  isEventPublic = true,
  communityShareMode = 'organiser',
  showCommunityShare = true,
  variant = 'outlined',
  size = 'large',
  disabled = false,
  sx,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { copyLink, shareToFacebook, shareToLinkedIn, shareToX, shareToInstagram, isLoading } = useEventShare();
  const notifications = useNotifications();
  const dialogs = useDialogs();

  const isShareable = isEventShareable(eventStatus);
  const isButtonDisabled = disabled || isLoading || !isShareable;

  const shareDisabledReason = !isShareable
    ? 'Share is available when the event is Scheduled, In Progress, or Completed.'
    : '';

  const getVisibilityWarning = (): string | null => {
    if (isInviteOnly && !isEventPublic) {
      return 'This event is invite-only and private. Only invited people can access it.';
    }

    if (isInviteOnly) {
      return 'This event is invite-only. People without an invitation cannot access it.';
    }

    if (!isEventPublic) {
      return 'This event is private. It is not publicly listed and is best shared intentionally.';
    }

    return null;
  };

  const showVisibilityWarning = () => {
    const warningMessage = getVisibilityWarning();
    if (!warningMessage) {
      return;
    }

    notifications.show(warningMessage, {
      severity: 'warning',
      autoHideDuration: 5000,
    });
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCopyLink = async () => {
    handleClose();
    showVisibilityWarning();

    const success = await copyLink({ eventId, eventTitle });

    if (success) {
      notifications.show('Event link copied to clipboard!', {
        severity: 'success',
        autoHideDuration: 3000,
      });
    } else {
      notifications.show('Failed to copy link. Please try again.', {
        severity: 'error',
        autoHideDuration: 4000,
      });
    }
  };

  const handleShareFacebook = async () => {
    handleClose();
    showVisibilityWarning();

    const success = await shareToFacebook({ eventId, eventTitle });

    if (!success) {
      notifications.show('Failed to share to Facebook. Please try again.', {
        severity: 'error',
        autoHideDuration: 4000,
      });
    }
  };

  const handleShareLinkedIn = async () => {
    handleClose();
    showVisibilityWarning();

    const success = await shareToLinkedIn({ eventId, eventTitle });

    if (!success) {
      notifications.show('Failed to share to LinkedIn. Please try again.', {
        severity: 'error',
        autoHideDuration: 4000,
      });
    }
  };

  const handleShareX = async () => {
    handleClose();
    showVisibilityWarning();

    const success = await shareToX({ eventId, eventTitle });

    if (!success) {
      notifications.show('Failed to share to X. Please try again.', {
        severity: 'error',
        autoHideDuration: 4000,
      });
    }
  };

  const handleShareToCommunities = async () => {
    handleClose();

    const results = await dialogs.open(ShareToCommunitiesDialog, { eventId, eventTitle, isInviteOnly, mode: communityShareMode });

    if (results?.length) {
      const communityCount = results.length;
      notifications.show(
        `Shared to ${communityCount} ${communityCount === 1 ? 'community' : 'communities'}.`,
        { severity: 'success', autoHideDuration: 4000 }
      );
    }
  };

  const handleShareInstagram = async () => {
    handleClose();
    showVisibilityWarning();

    const success = await shareToInstagram({ eventId, eventTitle });

    if (success) {
      notifications.show('Link copied! Paste it in your Instagram story or bio.', {
        severity: 'info',
        autoHideDuration: 5000,
      });
    } else {
      notifications.show('Failed to copy link for Instagram. Please try again.', {
        severity: 'error',
        autoHideDuration: 4000,
      });
    }
  };

  return (
    <>
      <Tooltip
        title={shareDisabledReason}
        disableHoverListener={!shareDisabledReason}
        placement="top"
      >
        <span>
          <Button
            variant={variant}
            size={size}
            startIcon={isLoading ? <CircularProgress size={20} /> : <Share />}
            onClick={handleClick}
            disabled={isButtonDisabled}
            fullWidth={size === 'large'} // Only full width for large size (sidebar buttons)
            sx={[
              {
                borderRadius: 2,
                fontWeight: 600,
                textTransform: "none",
                transition: "all 0.2s",
                '&.Mui-disabled': {
                  opacity: 1,
                  cursor: 'not-allowed',
                  pointerEvents: 'auto',
                  color: 'rgba(95, 79, 135, 0.58)',
                  borderColor: 'rgba(73, 49, 147, 0.28)',
                  backgroundColor: 'rgba(73, 49, 147, 0.11)',
                  '& .MuiButton-startIcon, & .MuiButton-startIcon .MuiSvgIcon-root': {
                    opacity: 0.5,
                  },
                },
                "&:hover": {
                  boxShadow: 3,
                  transform: "scale(1.05)"
                },
                // Sidebar buttons (large size)
                ...(size === 'large' && {
                  boxShadow: 1,
                  px: 2,
                  py: 1.2,
                }),
                // Header buttons (medium contained) — match other header buttons
                ...(variant === 'contained' && size === 'medium' && {
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  '&.Mui-disabled': {
                    opacity: 1,
                    cursor: 'not-allowed',
                    pointerEvents: 'auto',
                    color: 'rgba(255, 255, 255, 0.72)',
                    backgroundColor: 'rgba(255, 255, 255, 0.18)',
                    borderColor: 'rgba(255, 255, 255, 0.24)',
                    '& .MuiButton-startIcon, & .MuiButton-startIcon .MuiSvgIcon-root': {
                      opacity: 0.55,
                    },
                  },
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.3)',
                    boxShadow: 3,
                    transform: 'scale(1.05)'
                  },
                }),
              },
              // Apply any caller-provided overrides last
              ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
            ]}
          >
            Share Event
          </Button>
        </span>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              minWidth: 200,
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            },
          },
        }}
      >
        <MenuItem onClick={handleCopyLink} disabled={isLoading}>
          <ListItemIcon>
            <ContentCopy fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy Link</ListItemText>
        </MenuItem>

        <Divider sx={{ my: 1 }} />

        <MenuItem onClick={handleShareFacebook} disabled={isLoading}>
          <ListItemIcon>
            <Facebook fontSize="small" sx={{ color: '#1877F2' }} />
          </ListItemIcon>
          <ListItemText>Share to Facebook</ListItemText>
        </MenuItem>

        <MenuItem onClick={handleShareLinkedIn} disabled={isLoading}>
          <ListItemIcon>
            <LinkedIn fontSize="small" sx={{ color: '#0A66C2' }} />
          </ListItemIcon>
          <ListItemText>Share to LinkedIn</ListItemText>
        </MenuItem>

        <MenuItem onClick={handleShareX} disabled={isLoading}>
          <ListItemIcon>
            <XIcon fontSize="small" sx={{ color: '#000000' }} />
          </ListItemIcon>
          <ListItemText>Share to X</ListItemText>
        </MenuItem>

        <MenuItem onClick={handleShareInstagram} disabled={isLoading}>
          <ListItemIcon>
            <Instagram fontSize="small" sx={{ color: '#E4405F' }} />
          </ListItemIcon>
          <ListItemText>Share to Instagram</ListItemText>
        </MenuItem>

        {showCommunityShare && <Divider sx={{ my: 1 }} />}

        {showCommunityShare && <MenuItem onClick={handleShareToCommunities}>
          <ListItemIcon>
            <Groups fontSize="small" sx={{ color: 'primary.main' }} />
          </ListItemIcon>
          <ListItemText>Share to communities</ListItemText>
        </MenuItem>}
      </Menu>
    </>
  );
};

export default EventShareButton;
